import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FilterMatchMode } from 'primereact/api';
import { translatedMessage } from '../../service/LanguageService';
import useAuth from '../../hooks/useAuth';
import DataTableUtils from '../../utilities/DataTableUtils';
import DeleteModalComponent from '../DeleteModalComponent';
import ExportButtonComponent from '../ExportButtonComponent';
import GeneralUtils from '../../utilities/GeneralUtils';
import { CompetitionService } from '../../service/CompetitionService';
import SubjectProposalEditDialog from './SubjectProposalEditDialog';
import SubjectProposalViewDialog from './SubjectProposalViewDialog';

const tableHeader = [
    translatedMessage('generic.nameRo'),
    translatedMessage('competition.competition'),
    translatedMessage('generic.type'),
    translatedMessage('generic.startDate'),
    translatedMessage('generic.endDate'),
    translatedMessage('generic.status'),
    translatedMessage('subject.member.roles')
];

const SubjectProposalsListComponent = (props) => {
    const [allProposalsList, setAllProposalsList] = useState([]);
    const [selectedProposal, setSelectedProposal] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [viewDialogVisible, setViewDialogVisible] = useState(false);

    const [filters] = useState({
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        type: { value: null, matchMode: FilterMatchMode.IN },
        active: { value: null, matchMode: FilterMatchMode.IN }
    });

    const toast = useRef(null);
    const dt = useRef(null);
    const { auth } = useAuth();

    const competitionService = useMemo(() => new CompetitionService(), []);

    const hasPermission = (permissionName) => {
        if (auth && auth.user && auth.user.permissions) {
            return auth.user.permissions.indexOf(permissionName) > -1;
        } else {
            return false;
        }
    };

    useEffect(() => {
        setIsLoading(true);
        let _proposals = props.subjects ? props.subjects?.map((subject) => subject.proposals).flat() : [];
        setAllProposalsList(_proposals);
        let _selectedProposal = _proposals.find((_p) => _p.id === selectedProposal?.id);
        setSelectedProposal(_selectedProposal);

        setIsLoading(false);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props, competitionService]);

    const showNewDialog = () => {
        setSelectedProposal(null);
        setEditDialogVisible(true);
    };

    const showEditDialog = (subject) => {
        setSelectedProposal(subject);
        setEditDialogVisible(true);
    };

    const hideEditDialog = () => {
        setSelectedProposal(null);
        setEditDialogVisible(false);
    };

    const showViewSubjectDialog = (subject) => {
        setSelectedProposal(subject);
        setViewDialogVisible(true);
    };

    const showDeleteDialog = (subject) => {
        setSelectedProposal(subject);
        setDeleteDialogVisible(true);
    };

    const hideDeleteDialog = () => {
        setDeleteDialogVisible(false);
    };

    const deleteSubjectProposal = () => {
        competitionService
            .deleteSubjectProposal(selectedProposal.id)
            .then(() => {
                refreshData();
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.delete.success') });
            })
            .catch((error) => {
                toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            })
            .finally(() => {
                setDeleteDialogVisible(false);
                setSelectedProposal(null);
            });
    };

    const afterSaveSubjectProposal = async (savedSubjectProposal, isNew) => {
        if (isNew) {
            setSelectedProposal(savedSubjectProposal);
            setEditDialogVisible(true);
        }
        await refreshData();
    };

    const refreshData = async () => {
        competitionService
            .getCompetitionSubjects(props.competition?.id)
            .then((_subjects) => {
                let _allProposals = _subjects.map((subject) => subject.proposals).flat();
                setAllProposalsList(_allProposals);
            })
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                {props.permissions.canEditSubjectProposals && (
                    <div className="my-2">
                        <Button label={translatedMessage('competition.subjectProposals.add')} icon="pi pi-plus" className="p-button-primary mr-2" onClick={showNewDialog} />
                    </div>
                )}
            </React.Fragment>
        );
    };

    const handleExportRoles = (_roles) => {
        let response = '';

        _roles?.forEach((role) => {
            response += response ? '; ' : '';
            response += translatedMessage('subject.member.' + role.name) + ' (' + role.number + ')';
        });

        return response;
    };

    const handleExport = () => {
        let exportData = allProposalsList.map((item) => ({ ...item }));
        exportData.map((item) => {
            let exportItem = item;
            exportItem.active = translatedMessage('subject.' + (item.active ? 'ACTIVE' : 'INACTIVE'));
            exportItem.type = translatedMessage('EvaluationSubjectType.' + item.type);
            exportItem.addedRoles = handleExportRoles(item.addedRoles);
            delete exportItem.competitionId;
            delete exportItem.competitionType;
            delete exportItem.folderId;
            delete exportItem.description;

            return item;
        });

        return exportData;
    };

    const rightToolbarTemplate = () => {
        //return <ExportButtonComponent getExportData={handleExport} header={tableHeader} fileName={GeneralUtils.replaceSpaces(translatedMessage('competition.subjectProposals'))} />;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                {(hasPermission('COMPETITION_EDIT') || hasPermission('COMPETITION_VIEW')) && (
                    <Button icon="pi pi-eye" className="p-button-rounded p-button-primary m-1" onClick={() => showViewSubjectDialog(rowData)} tooltip={translatedMessage('generic.view')} tooltipOptions={{ showOnDisabled: true, position: 'top' }} />
                )}
                {auth && auth.user && auth.user.id === rowData.author.userId && (
                    <>
                        {props.permissions.canEditSubjectProposals && (hasPermission('COMPETITION_EDIT') || hasPermission('COMPETITION_VIEW')) && (
                            <Button icon="pi pi-pencil" className="p-button-rounded p-button-info m-1" onClick={() => showEditDialog(rowData)} tooltip={translatedMessage('generic.edit')} tooltipOptions={{ showOnDisabled: true, position: 'top' }} />
                        )}
                        {props.permissions.canEditSubjectProposals && (
                            <Button
                                icon="pi pi-trash"
                                className="p-button-rounded p-button-danger m-1"
                                onClick={() => showDeleteDialog(rowData)}
                                disabled={rowData.id < 1000}
                                tooltip={translatedMessage('generic.delete')}
                                tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                            />
                        )}
                    </>
                )}
            </div>
        );
    };

    const authorBodyTemplate = (rowData) => {
        return (
            <>
                {rowData.author.fullName} (
                {rowData.author.isPresident
                    ? translatedMessage('committee.member.isPresident')
                    : rowData.author.isSecretary
                      ? translatedMessage('committee.member.isSecretary')
                      : rowData.author.isSimpleMember
                        ? translatedMessage('committee.member.isSimpleMember')
                        : translatedMessage('committee.member.noRole')}
                )
            </>
        );
    };

    const questionsScoreBodyTemplate = (rowData) => {
        return rowData.questions.map((question) => question.score).reduce((a, b) => a + b, 0);
    };

    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div className='mt-5'>
                    <h5 className="mb-1 font-weight-normal">{translatedMessage('competition.subjectProposals')}</h5>
                </div>
                <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="px-0 pt-0"></Toolbar>

                <DataTable
                    ref={dt}
                    value={allProposalsList}
                    dataKey="id"
                    paginator
                    rows={DataTableUtils.defalRowsPerPage()}
                    rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                    className="datatable-responsive pcn-datatable"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={'{first} - {last} ' + translatedMessage('generic.of') + ' {totalRecords}'}
                    emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                    responsiveLayout="stack"
                    // filterDisplay="row"
                    // filters={filters}
                    // globalFilterFields={['name', 'type', 'active']}
                >
                    <Column field="subject.name" header={translatedMessage('competition.subject')} sortable headerStyle={{ width: '30%', minWidth: '10rem' }} />
                    <Column field="author.fullName" body={authorBodyTemplate} header={translatedMessage('competition.subjectProposal.author')} sortable headerStyle={{ width: '30%', minWidth: '10rem' }} />
                    <Column field="questions.length" header={translatedMessage('competition.subjectProposal.questionsNumber')} sortable headerStyle={{ width: '10%', minWidth: '10rem' }} />
                    <Column field="questions.length" body={questionsScoreBodyTemplate} header={translatedMessage('competition.subjectProposal.questionsScore')} sortable headerStyle={{ width: '10%', minWidth: '10rem' }} />
                    <Column body={actionBodyTemplate}/>
                </DataTable>

                <SubjectProposalViewDialog value={selectedProposal} visible={viewDialogVisible} visibleSetter={setViewDialogVisible} afterSave={setViewDialogVisible} />

                <SubjectProposalEditDialog
                    value={selectedProposal}
                    visible={editDialogVisible}
                    visibleSetter={setEditDialogVisible}
                    afterSave={afterSaveSubjectProposal}
                    subjects={props.subjects}
                    canEdit={props.canEdit}
                    questionTypes={props.questionTypes}
                    competition={props.competition}
                    permissions={props.permissions}
                />

                {deleteDialogVisible && <DeleteModalComponent visible={deleteDialogVisible} item={selectedProposal ? selectedProposal.name : ''} closeDialog={hideDeleteDialog} deleteRecord={() => deleteSubjectProposal()} />}
            </>
        );
    }
};

export default SubjectProposalsListComponent;
